package com.castly.admin.ui.actions

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppColors
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AuditActorDto
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

data class AuditLogFilterState(
    val keyword: String = "",
    val actionType: String = "",
    val entityType: String = "",
    val dateFrom: String = "",
    val dateTo: String = "",
    val actorId: String = "",
    val labelName: String = ""
) {
    val activeCount: Int
        get() = listOf(keyword, actionType, entityType, dateFrom, dateTo, actorId, labelName).count { it.isNotBlank() }
    val isActive: Boolean get() = activeCount > 0
}

private val ACTION_TYPES = listOf(
    "" to "All actions",
    "Create" to "Create",
    "Update" to "Update",
    "Delete" to "Delete",
    "Reorder" to "Reorder",
    "Publish" to "Publish",
    "Unpublish" to "Unpublish",
    "Import" to "Import",
    "BulkUpdate" to "Bulk Update",
    "Login" to "Login",
    "Logout" to "Logout"
)

private val ENTITY_TYPES = listOf(
    "" to "All entities",
    "Podcast" to "Podcast",
    "Episode" to "Episode",
    "Audiobook" to "Audiobook",
    "AudiobookEpisode" to "Audiobook Episode",
    "Tag" to "Tag",
    "HomeSection" to "Home Section",
    "Author" to "Author",
    "Reader" to "Reader",
    "AdminUser" to "Admin User",
    "Session" to "Session"
)

@Composable
fun AuditLogFilters(
    filterState: AuditLogFilterState,
    onFilterChange: (AuditLogFilterState) -> Unit,
    isAdmin: Boolean = false,
    actors: List<AuditActorDto> = emptyList()
) {
    val colors = AppTheme.colors
    var isExpanded by remember { mutableStateOf(false) }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            border(1.px, LineStyle.Solid, Color(colors.border))
            marginBottom(Spacing.lg)
            overflow("hidden")
            themeTransition()
        }
    }) {
        // Filter header / toggle
        Div({
            onClick { isExpanded = !isExpanded }
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                padding(Spacing.md, Spacing.lg)
                cursor("pointer")
                backgroundColor(Color(colors.surfaceHover))
                themeTransition()
            }
        }) {
            // Left: label + active count badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.sm)
                }
            }) {
                Span({
                    style {
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(colors.text))
                    }
                }) { Text("Filters") }

                if (filterState.activeCount > 0) {
                    Span({
                        style {
                            display(DisplayStyle.LegacyInlineFlex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            backgroundColor(Color(colors.primary))
                            color(Color("#ffffff"))
                            borderRadius(BorderRadius.pill)
                            fontSize(Typography.xs)
                            fontWeight("700")
                            minWidth(18.px)
                            height(18.px)
                            padding(0.px, 4.px)
                        }
                    }) { Text("${filterState.activeCount}") }
                }
            }

            // Right: clear button + chevron
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(Spacing.md)
                }
            }) {
                if (filterState.isActive) {
                    Button({
                        onClick { event ->
                            event.stopPropagation()
                            onFilterChange(AuditLogFilterState())
                        }
                        style {
                            padding(Spacing.xs, Spacing.md)
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            backgroundColor(Color("transparent"))
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            borderRadius(BorderRadius.sm)
                            cursor("pointer")
                        }
                    }) { Text("Clear Filters") }
                }

                Span({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                        property("transition", "transform 0.2s ease")
                        property("transform", if (isExpanded) "rotate(180deg)" else "rotate(0deg)")
                        property("display", "inline-block")
                    }
                }) { Text("▾") }
            }
        }

        // Expanded filter fields
        if (isExpanded) {
            Div({
                style {
                    padding(Spacing.lg)
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", if (isAdmin) "2fr 1fr 1fr 1fr 1fr 1fr 1fr" else "2fr 1fr 1fr 1fr 1fr")
                    gap(Spacing.md)
                    property("border-top", "1px solid ${colors.border}")
                }
            }) {
                // Keyword search
                FilterField("Keyword", colors) {
                    Input(InputType.Text) {
                        value(filterState.keyword)
                        onInput { onFilterChange(filterState.copy(keyword = it.value)) }
                        attr("placeholder", "Search by name, ID, or actor…")
                        style { filterInputStyle(colors) }
                    }
                }

                // Action type dropdown
                FilterField("Action Type", colors) {
                    Select({
                        onInput { onFilterChange(filterState.copy(actionType = it.value ?: "")) }
                        style { filterInputStyle(colors) }
                    }) {
                        ACTION_TYPES.forEach { (value, label) ->
                            Option(value, { if (filterState.actionType == value) selected() }) {
                                Text(label)
                            }
                        }
                    }
                }

                // Entity type dropdown
                FilterField("Entity Type", colors) {
                    Select({
                        onInput { onFilterChange(filterState.copy(entityType = it.value ?: "")) }
                        style { filterInputStyle(colors) }
                    }) {
                        ENTITY_TYPES.forEach { (value, label) ->
                            Option(value, { if (filterState.entityType == value) selected() }) {
                                Text(label)
                            }
                        }
                    }
                }

                // Date from
                FilterField("From Date", colors) {
                    Input(InputType.Date) {
                        value(filterState.dateFrom)
                        onInput { onFilterChange(filterState.copy(dateFrom = it.value)) }
                        style { filterInputStyle(colors) }
                    }
                }

                // Date to
                FilterField("To Date", colors) {
                    Input(InputType.Date) {
                        value(filterState.dateTo)
                        onInput { onFilterChange(filterState.copy(dateTo = it.value)) }
                        style { filterInputStyle(colors) }
                    }
                }

                // Label filter — ADMIN / Label Admin only
                if (isAdmin) {
                    FilterField("Label", colors) {
                        Input(InputType.Text) {
                            value(filterState.labelName)
                            onInput { onFilterChange(filterState.copy(labelName = it.value)) }
                            attr("placeholder", "Filter by label name…")
                            style { filterInputStyle(colors) }
                        }
                    }
                }

                // Actor filter — ADMIN only
                if (isAdmin) {
                    FilterField("Actor", colors) {
                        Select({
                            onInput { onFilterChange(filterState.copy(actorId = it.value ?: "")) }
                            style { filterInputStyle(colors) }
                        }) {
                            Option("", { if (filterState.actorId.isBlank()) selected() }) {
                                Text("All actors")
                            }
                            actors.forEach { actor ->
                                Option(actor.actorId, { if (filterState.actorId == actor.actorId) selected() }) {
                                    Text(actor.actorDisplayName)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterField(label: String, colors: AppColors, content: @Composable () -> Unit) {
    Div {
        Span({
            style {
                display(DisplayStyle.Block)
                fontSize(Typography.xs)
                fontWeight("600")
                color(Color(colors.textMuted))
                property("text-transform", "uppercase")
                property("letter-spacing", "0.05em")
                marginBottom(Spacing.xs)
            }
        }) { Text(label) }
        content()
    }
}

private fun StyleScope.filterInputStyle(colors: AppColors) {
    width(100.percent)
    padding(Spacing.sm, Spacing.md)
    fontSize(Typography.base)
    color(Color(colors.text))
    backgroundColor(Color(colors.surface))
    border(1.px, LineStyle.Solid, Color(colors.border))
    borderRadius(BorderRadius.md)
    property("outline", "none")
    property("box-sizing", "border-box")
    themeTransition()
}
